import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ProfissionalPerformance } from '@/hooks/useGestorPerformance';

interface TeamPerformanceChartProps {
  data: ProfissionalPerformance[];
}

const roleColors: Record<string, string> = {
  'ENG': 'hsl(215, 28%, 40%)',
  'TEC': 'hsl(180, 15%, 50%)',
  'SMS': 'hsl(38, 50%, 55%)',
  'PL': 'hsl(145, 50%, 40%)'
};

const roleLabels: Record<string, string> = {
  'ENG': 'Engenheiro',
  'TEC': 'Técnico',
  'SMS': 'SMS',
  'PL': 'Planejador'
};

const TeamPerformanceChart: React.FC<TeamPerformanceChartProps> = ({ data }) => {
  // Agrupar por função
  const performancePorRole = data.reduce((acc, prof) => {
    if (!acc[prof.role]) {
      acc[prof.role] = { total: 0, agendadas: 0, concluidas: 0 };
    }
    acc[prof.role].total++;
    acc[prof.role].agendadas += prof.total_agendadas;
    acc[prof.role].concluidas += prof.total_concluidas;
    return acc;
  }, {} as Record<string, { total: number; agendadas: number; concluidas: number }>);

  const chartData = Object.entries(performancePorRole).map(([role, values]) => ({
    name: roleLabels[role] || role,
    profissionais: values.total,
    agendadas: values.agendadas,
    concluidas: values.concluidas,
    taxa: values.agendadas > 0 ? Math.round((values.concluidas / values.agendadas) * 100) : 0,
    color: roleColors[role] || 'hsl(0, 0%, 50%)'
  }));

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium">Desempenho por Função</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
            Nenhum dado disponível
          </div>
        ) : (
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 11 }} 
                  tickLine={false} 
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={35}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={(value: number, name: string) => {
                    if (name === 'agendadas') return [value, 'Agendadas'];
                    if (name === 'concluidas') return [value, 'Concluídas'];
                    return [value, name];
                  }}
                />
                <Legend
                  verticalAlign="top"
                  height={28}
                  formatter={(value) => (
                    <span className="text-xs text-foreground">
                      {value === 'agendadas' ? 'Agendadas' : 'Concluídas'}
                    </span>
                  )}
                />
                <Bar 
                  dataKey="agendadas" 
                  fill="hsl(var(--muted-foreground))" 
                  radius={[4, 4, 0, 0]} 
                  maxBarSize={40}
                  opacity={0.5}
                />
                <Bar 
                  dataKey="concluidas" 
                  radius={[4, 4, 0, 0]} 
                  maxBarSize={40}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamPerformanceChart;
